// Shared display formatters — pilot for the future @navio-maritime/ui kit.
// One way to render money / numbers / dates across every NavioBuild page, so the
// whole product formats values identically. (Tier 1 of the shared-UI-kit plan.)

const LOCALE = 'en-GB';

function toDisplayDate(value: string | Date): Date {
  if (value instanceof Date) return value;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return new Date(`${value}T00:00:00`);
  return new Date(value);
}

/** Money, compact by default (no decimals — yacht-build sums are large). */
export function formatCurrency(
  amount: number | null | undefined,
  currency = 'EUR',
  options: { maximumFractionDigits?: number } = {}
): string {
  if (amount == null || !Number.isFinite(amount)) return '—';
  return new Intl.NumberFormat(LOCALE, {
    style: 'currency',
    currency,
    maximumFractionDigits: options.maximumFractionDigits ?? 0,
  }).format(amount);
}

/** Plain number with thousands separators. */
export function formatNumber(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return new Intl.NumberFormat(LOCALE).format(value);
}

/** Date as `dd Mmm yyyy` (e.g. 24 Jun 2026). */
export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = toDisplayDate(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(LOCALE, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

/** Compact date for dense tables: `24/06/2026`. */
export function formatNumericDate(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = toDisplayDate(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(LOCALE, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
}

/** Timeline header date: `24 Jun`. */
export function formatShortDate(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = toDisplayDate(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(LOCALE, {
    day: 'numeric',
    month: 'short',
  }).format(date);
}

/** Timeline month bucket: `Jun 26`. */
export function formatShortMonthYear(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = toDisplayDate(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(LOCALE, {
    month: 'short',
    year: '2-digit',
  }).format(date);
}

/** Audit/event timestamp: `24 Jun 2026, 14:30`. */
export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = toDisplayDate(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(LOCALE, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

/** Time-only label for operational timestamps: `14:30`. */
export function formatTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = toDisplayDate(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(LOCALE, {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}
